import * as leadRepository from "../repositories/leadRepository.js";
import api from "../../../shared/api.jsx";
import { groupLeadsByPhone, flattenGroupedLeads } from "../utils/leadDeduplication.js";
import { logError, logInfo } from "@/shared/utils/logger.js";

/**
 * Leads Service - Business logic for lead management
 */

/**
 * Get all leads with optional filters
 * @param {Object} filters - Filter options
 * @param {string} [filters.status] - Lead status filter
 * @param {string} [filters.source] - Lead source filter
 * @param {string} [filters.date] - Date filter
 * @param {number} [filters.limit] - Limit results
 * @param {number} [filters.offset] - Offset for pagination
 * @returns {Promise<Object>} Leads data
 */
export const getLeads = async (filters) => {
  const response = await leadRepository.fetchLeads(filters);
  if (!response.success)
    throw new Error(response.error || "Failed to fetch leads");
  return {
    leads: response.leads,
    count: response.count,
    success: true,
  };
};

/**
 * Get leads grouped by phone number (deduplicated)
 * @param {Object} filters - Filter options (same as getLeads)
 * @param {boolean} [flatten=true] - Whether to flatten grouped leads for display
 * @returns {Promise<Object>} Grouped leads data
 */
export const getLeadsGrouped = async (filters = {}, flatten = true) => {
  try {
    logInfo("Fetching grouped leads", { filters });
    
    // Fetch leads using existing service
    const { leads, count } = await getLeads(filters);
    
    // Group by phone number
    const groupedData = groupLeadsByPhone(leads);
    
    // Flatten for display if requested
    const displayLeads = flatten ? flattenGroupedLeads(groupedData) : groupedData;
    
    logInfo("Leads grouped successfully", {
      originalCount: leads.length,
      groupedCount: groupedData.grouped.length,
      ungroupedCount: groupedData.ungrouped.length,
      displayCount: flatten ? displayLeads.length : groupedData.grouped.length + groupedData.ungrouped.length,
    });
    
    return {
      leads: displayLeads,
      grouped: groupedData.grouped,
      ungrouped: groupedData.ungrouped,
      count: count || leads.length,
      success: true,
    };
  } catch (error) {
    logError("Failed to get grouped leads", {
      filters,
      error: error.message,
      stack: error.stack,
    });
    throw error;
  }
};

/**
 * Get a single lead by ID
 * @param {string} leadId - Lead identifier
 * @returns {Promise<Object>} Lead data
 */
export const getLead = async (leadId) => {
  try {
    const response = await leadRepository.fetchLead(leadId);
    // Repository returns { success, data }; keep that shape for callers
    if (response?.success === false) {
      throw new Error(response.error || "Failed to fetch lead");
    }
    return {
      success: true,
      data: response?.data ?? response,
    };
  } catch (error) {
    console.error("Error fetching lead:", error);
    throw error;
  }
};

/**
 * Get lead statistics
 * @returns {Promise<Object>} Lead statistics
 */
export const getLeadStats = async () => {
  try {
    const response = await leadRepository.fetchLeadStats();
    return response;
  } catch (error) {
    console.error("Error fetching lead stats:", error);
    throw error;
  }
};

/**
 * Accept a lead
 * @param {string} leadId - Lead identifier
 * @param {string} [reason] - Reason for acceptance
 * @param {string} [notes] - Additional notes
 * @returns {Promise<Object>} Update result
 */
export const acceptLead = async (leadId, reason = "", notes = "") => {
  try {
    const response = await leadRepository.updateLeadStatus(leadId, {
      action: "accept",
      reason,
      notes,
    });
    return response;
  } catch (error) {
    console.error("Error accepting lead:", error);
    throw error;
  }
};

/**
 * Reject a lead
 * @param {string} leadId - Lead identifier
 * @param {string} [reason] - Reason for rejection
 * @param {string} [notes] - Additional notes
 * @returns {Promise<Object>} Update result
 */
export const rejectLead = async (leadId, reason = "", notes = "") => {
  try {
    const response = await leadRepository.updateLeadStatus(leadId, {
      action: "reject",
      reason,
      notes,
    });
    return response;
  } catch (error) {
    console.error("Error rejecting lead:", error);
    throw error;
  }
};

/**
 * Convert a lead to quote
 * @param {string} leadId - Lead identifier
 * @param {string} [reason] - Reason for conversion
 * @param {string} [notes] - Additional notes
 * @returns {Promise<Object>} Update result
 */
export const convertLeadToQuote = async (leadId, reason = "", notes = "") => {
  try {
    // Fetch lead data first (needed for quote details)
    // Note: We don't pre-update the lead status here - the backend conversion service
    // will automatically set the lead status to "accepted" when the quote is created
    const leadResponse = await leadRepository.fetchLead(leadId);
    const leadData = leadResponse.data || leadResponse;

    // Support both legacy move_date and new move_dates array
    const datesArray = leadData.move_dates && leadData.move_dates.length > 0
      ? leadData.move_dates.filter((d) => d && d.trim() !== "")
      : (leadData.move_date ? [leadData.move_date] : ["Not specified"]);

    const quoteDetails = {
      from: leadData.moving_from || "Not specified",
      to: leadData.moving_to || "Not specified",
      date: datesArray[0] || leadData.move_date || "Not specified", // Keep for backward compatibility
      dates: datesArray, // New: array of dates
      items: leadData.items || [],
      notes: notes || leadData.notes || `Converted from lead ${leadId}`,
    };

    // Call the backend conversion service
    // The backend will automatically:
    // 1. Create the quote
    // 2. Set lead status to "accepted" (so it appears in the accepted column)
    // 3. Set lifecycleStage to "converted" (for tracking)
    const conversionResult = await api.post("/conversion/lead-to-quote", {
      leadId,
      quoteDetails,
    });

    return {
      success: true,
      message: "Lead successfully converted to quote",
      data: conversionResult.data,
      quoteId: conversionResult.data?.quoteId || conversionResult.quoteId,
    };
  } catch (error) {
    console.error("Error converting lead to quote:", error);
    throw error;
  }
};

/**
 * Update lead information
 * @param {string} leadId - Lead identifier
 * @param {Object} updateData - Update data
 * @returns {Promise<Object>} Update result
 */
export const updateLead = async (leadId, updateData) => {
  try {
    const response = await leadRepository.updateLead(leadId, updateData);
    return response;
  } catch (error) {
    console.error("Error updating lead:", error);
    throw error;
  }
};
